/* eslint-disable import/prefer-default-export */
import { centerOfMass } from '@turf/turf';

export function getFeatureLabels(properties = {}) {
  const {
    $landType,
    name,
    cropzones,
    $includedChildGeometries,
    $parentDuplicated
  } = properties;

  const defaultLabel = $landType === 'field' ? name || 'Untitled-Field' : '';

  let fieldAndCropZoneLabel;
  if ($landType === 'field') {
    if (cropzones?.length) {
      // Field has crop zones: don't repeat the field name in the center.
      // Instead, stack the names of any full-field crop zones (those whose
      // geometry matches the field) at the field center. Sub-field crop zones
      // render their own labels over their sub-boundaries.
      const includedIds = $includedChildGeometries || [];
      fieldAndCropZoneLabel = cropzones
        .filter(cropZone => includedIds.includes(cropZone.id))
        .map(cropZone => cropZone.name || 'Untitled')
        .join('\n');
    } else {
      fieldAndCropZoneLabel = name || 'Untitled-Field';
    }
  } else {
    // Crop zone: full-field crop zones share the field's centroid, so their
    // names are already stacked on the field center — suppress the colliding
    // label here. Sub-field crop zones show their own name over their boundary.
    fieldAndCropZoneLabel = $parentDuplicated ? '' : name || 'Untitled-Field';
  }

  return { defaultLabel, fieldAndCropZoneLabel };
}

export function formatGeoJSON(geoJSON) {
  // Drop label features left over from a previous run. formatGeoJSON injects a
  // separate label Point per field/crop zone, and selecting a farm/field merges
  // the prior output back in and reformats it. Those labels carry no
  // $landType/name, so reprocessing them yields spurious "Untitled-Field"
  // labels and duplicate empties — filtering them keeps the function idempotent.
  const sourceFeatures = geoJSON.features.filter(
    feature => !feature.properties?.$isLabel
  );
  return {
    ...geoJSON,
    features: sourceFeatures.flatMap(feature => {
      // Check if the feature has geometry
      if (!feature.geometry) {
        const { defaultLabel, fieldAndCropZoneLabel } = getFeatureLabels(
          feature.properties
        );

        return {
          ...feature,
          properties: {
            ...feature.properties,
            defaultLabel,
            fieldAndCropZoneLabel
          }
        };
      }

      const center = centerOfMass(feature);
      const labelPosition = center.geometry.coordinates;

      const { defaultLabel, fieldAndCropZoneLabel } = getFeatureLabels(
        feature.properties
      );

      const labelFeature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: labelPosition
        },
        properties: {
          $isLabel: true,
          $sourceFeatureId: feature.properties?.id,
          defaultLabel,
          fieldAndCropZoneLabel
        }
      };

      return [feature, labelFeature];
    })
  };
}
